/**
 * DISCOM Analyser — National role screen
 * FR-N13 to FR-N17
 */
import React, { useMemo, useState } from 'react';
import { ChevronRight, Download } from 'lucide-react';
import { nationalDISCOMs, nationalKPIs, pgvclCircles } from '../../data/mockData';

const prCls = (pr: number) =>
  pr >= 75 ? 'text-green-600 font-semibold' :
  pr >= 65 ? 'text-amber-600 font-semibold' :
             'text-red-600 font-semibold';

// ── Tile definitions ──────────────────────────────────────────────────────────

interface TileDef {
  label: string;
  sub: string;
  textCls: string;
  bgCls: string;
  ringCls: string;
  filterFn: (d: typeof nationalDISCOMs[0]) => boolean;
}

const TILES: TileDef[] = [
  {
    label: 'PR below 70%', sub: 'Avg PR < 70%',
    textCls: 'text-red-600', bgCls: 'bg-red-50', ringCls: 'ring-red-500',
    filterFn: d => d.prPercent < 70,
  },
  {
    label: 'Low specific yield', sub: `Below ${nationalKPIs.avgSpecificYield} kWh/kWp`,
    textCls: 'text-teal-600', bgCls: 'bg-teal-50', ringCls: 'ring-teal-500',
    filterFn: d => d.specificYield < nationalKPIs.avgSpecificYield,
  },
  {
    label: 'Generation dropping MoM', sub: 'Current < last month',
    textCls: 'text-amber-600', bgCls: 'bg-amber-50', ringCls: 'ring-amber-500',
    filterFn: d => d.momTrend < 0,
  },
  {
    label: 'Low availability', sub: '<80% plants active',
    textCls: 'text-green-700', bgCls: 'bg-green-50', ringCls: 'ring-green-500',
    filterFn: d => d.activePercent < 80,
  },
  {
    label: 'High offline rate', sub: '>5% plants offline',
    textCls: 'text-orange-600', bgCls: 'bg-orange-50', ringCls: 'ring-orange-500',
    filterFn: d => (1 - d.activePercent / 100) > 0.05,
  },
  {
    label: 'High alarm density', sub: 'Alarms per plant > 0.5',
    textCls: 'text-purple-600', bgCls: 'bg-purple-50', ringCls: 'ring-purple-500',
    filterFn: d => d.alarms / d.plants > 0.5,
  },
];

const tileCounts = TILES.map(t => nationalDISCOMs.filter(t.filterFn).length);

// Unique states for cascading filter
const ALL_STATES = Array.from(new Set(nationalDISCOMs.map(d => d.state)));

type BreadcrumbLevel = 'national' | 'discom' | 'circle';

export default function NationalDISCOMAnalyser() {
  const [activeTile, setActiveTile]     = useState<number | null>(null);
  const [stateFilter, setStateFilter]   = useState('');
  const [discomFilter, setDiscomFilter] = useState('');
  const [circleFilter, setCircleFilter] = useState('');
  const [level, setLevel]               = useState<BreadcrumbLevel>('national');
  const [selectedDISCOM, setSelectedDISCOM] = useState<string | null>(null);

  // DISCOMs filtered by state + active tile
  const visibleDISCOMs = useMemo(() => {
    let list = nationalDISCOMs;
    if (stateFilter)  list = list.filter(d => d.state === stateFilter);
    if (activeTile !== null) list = list.filter(TILES[activeTile].filterFn);
    return list;
  }, [stateFilter, activeTile]);

  // Available DISCOMs for dropdown (state-filtered only, not tile-filtered)
  const discomOptions = nationalDISCOMs.filter(d => !stateFilter || d.state === stateFilter);

  const exportCSV = () => {
    const rows = ['DISCOM,State,Plants,GWp,GWh Today,Sp.Yield,PR%,Avail%,Alarms',
      ...visibleDISCOMs.map(d =>
        `${d.name},${d.state},${d.plants},${d.capacityGWp.toFixed(2)},${d.generationGWh.toFixed(2)},${d.specificYield},${d.prPercent},${d.activePercent},${d.alarms}`)];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href: url, download: 'discom-analyser.csv' });
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetToNational = () => {
    setLevel('national'); setSelectedDISCOM(null); setDiscomFilter(''); setCircleFilter('');
  };

  // Breadcrumb path
  const breadcrumb: { label: string; level: BreadcrumbLevel }[] = [
    { label: 'National', level: 'national' },
    ...(selectedDISCOM ? [{ label: selectedDISCOM, level: 'discom' as BreadcrumbLevel }] : []),
    ...(circleFilter && level === 'circle' ? [{ label: circleFilter, level: 'circle' as BreadcrumbLevel }] : []),
  ];

  return (
    <div className="p-6 space-y-5">

      {/* ── Triage tiles ── */}
      <div className="grid grid-cols-6 gap-3">
        {TILES.map((t, i) => {
          const isActive = activeTile === i;
          return (
            <div
              key={t.label}
              onClick={() => setActiveTile(prev => prev === i ? null : i)}
              className={`rounded-card border p-4 cursor-pointer transition-all hover:shadow-md ${t.bgCls}
                ${isActive ? `ring-2 ${t.ringCls} border-transparent` : 'border-border shadow-card'}`}
            >
              <p className="text-xs text-text-secondary font-semibold leading-tight">{t.label}</p>
              <p className={`text-2xl font-bold mt-1 ${t.textCls}`}>{tileCounts[i]}</p>
              <p className="text-xs text-text-tertiary mt-1">{t.sub}</p>
            </div>
          );
        })}
      </div>

      {/* ── Cascading filter bar ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={stateFilter}
          onChange={e => { setStateFilter(e.target.value); setDiscomFilter(''); resetToNational(); }}
          className="text-xs border border-border rounded-input px-2.5 py-1.5 bg-white focus:outline-none focus:border-accent"
        >
          <option value="">All States</option>
          {ALL_STATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {stateFilter && (
          <select
            value={discomFilter}
            onChange={e => {
              const val = e.target.value;
              setDiscomFilter(val);
              if (val) { setSelectedDISCOM(val); setLevel('discom'); } else { resetToNational(); }
            }}
            className="text-xs border border-border rounded-input px-2.5 py-1.5 bg-white focus:outline-none focus:border-accent"
          >
            <option value="">All DISCOMs</option>
            {discomOptions.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        )}

        {discomFilter && selectedDISCOM === 'PGVCL' && (
          <select
            value={circleFilter}
            onChange={e => { setCircleFilter(e.target.value); if (e.target.value) setLevel('circle'); else setLevel('discom'); }}
            className="text-xs border border-border rounded-input px-2.5 py-1.5 bg-white focus:outline-none focus:border-accent"
          >
            <option value="">All Circles</option>
            {pgvclCircles.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        )}

        {activeTile !== null && (
          <button onClick={() => setActiveTile(null)}
            className="text-xs text-accent hover:underline px-2 py-1 border border-accent/30 rounded-input">
            Clear filter
          </button>
        )}
        <div className="flex-1" />
        <button onClick={exportCSV}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-input border border-border bg-white text-text-secondary hover:text-accent hover:border-accent transition-colors">
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* ── Breadcrumb ── */}
      {level !== 'national' && (
        <div className="flex items-center gap-1 text-xs text-text-secondary">
          {breadcrumb.map((crumb, i) => (
            <React.Fragment key={crumb.label}>
              {i > 0 && <ChevronRight size={11} className="text-text-tertiary" />}
              {i < breadcrumb.length - 1 ? (
                <button
                  onClick={() => { setLevel(crumb.level); if (crumb.level === 'national') resetToNational(); if (crumb.level === 'discom') { setCircleFilter(''); setLevel('discom'); } }}
                  className="text-accent hover:underline font-medium"
                >
                  {crumb.label}
                </button>
              ) : (
                <span className="font-medium text-text-primary">{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* ── Table ── */}
      <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <p className="text-xs font-semibold text-text-primary">
            {level === 'national'
              ? `${visibleDISCOMs.length} DISCOM${visibleDISCOMs.length !== 1 ? 's' : ''}${activeTile !== null ? ` · ${TILES[activeTile].label}` : ''}`
              : level === 'discom'
                ? `Circles — ${selectedDISCOM}`
                : `Divisions — ${circleFilter}`
            }
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary border-b border-border">
              <tr>
                <th className="text-left py-2.5 px-4 text-text-secondary font-semibold">Name</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">State</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Plants</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Capacity</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Gen today</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Sp. yield</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">PR %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Avail %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">CUF %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">MoM %</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Active / Offline</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Alarms</th>
              </tr>
            </thead>
            <tbody>
              {level === 'national' && visibleDISCOMs.map(d => {
                const activePl  = Math.round(d.plants * d.activePercent / 100);
                const offlinePl = d.plants - activePl;
                return (
                  <tr key={d.id} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                    <td className="py-2.5 px-4">
                      <button
                        onClick={() => { setSelectedDISCOM(d.name); setDiscomFilter(d.name); setStateFilter(d.state); setLevel('discom'); }}
                        className="font-medium text-accent hover:underline text-left"
                      >
                        {d.name}
                      </button>
                    </td>
                    <td className="py-2.5 px-3 text-text-secondary">{d.state}</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{d.plants.toLocaleString()}</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{d.capacityGWp.toFixed(2)} GWp</td>
                    <td className="py-2.5 px-3 text-right font-medium text-text-primary">{d.generationGWh.toFixed(2)} GWh</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{d.specificYield} kWh/kWp</td>
                    <td className={`py-2.5 px-3 text-right ${prCls(d.prPercent)}`}>{d.prPercent.toFixed(1)}%</td>
                    <td className={`py-2.5 px-3 text-right ${d.activePercent < 80 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{d.activePercent.toFixed(0)}%</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{(d.prPercent * 0.22).toFixed(1)}%</td>
                    <td className={`py-2.5 px-3 text-right font-semibold ${d.momTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {d.momTrend >= 0 ? '↑' : '↓'}{Math.abs(d.momTrend).toFixed(1)}%
                    </td>
                    <td className="py-2.5 px-3 text-xs text-text-secondary">
                      {activePl.toLocaleString()} active · <span className={offlinePl > d.plants * 0.05 ? 'text-red-600 font-semibold' : ''}>{offlinePl.toLocaleString()} offline</span>
                    </td>
                    <td className={`py-2.5 px-3 text-right ${d.alarms > 100 ? 'text-red-600 font-semibold' : 'text-text-secondary'}`}>{d.alarms}</td>
                  </tr>
                );
              })}

              {/* Circle-level drill-down (PGVCL only) */}
              {level === 'discom' && selectedDISCOM === 'PGVCL' && pgvclCircles.map(c => {
                const activePl  = Math.round(c.plants * c.activePercent / 100);
                const offlinePl = c.plants - activePl;
                return (
                  <tr key={c.id} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                    <td className="py-2.5 px-4 font-medium text-text-primary">{c.name}</td>
                    <td className="py-2.5 px-3 text-text-secondary">Gujarat</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{c.plants.toLocaleString()}</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{(c.plants * 0.003).toFixed(2)} GWp</td>
                    <td className="py-2.5 px-3 text-right font-medium text-text-primary">{(c.generationMWh / 1000).toFixed(2)} GWh</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{(c.generationMWh / c.plants / 3).toFixed(2)} kWh/kWp</td>
                    <td className={`py-2.5 px-3 text-right ${prCls(c.prPercent)}`}>{c.prPercent.toFixed(1)}%</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{c.activePercent.toFixed(0)}%</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{(c.prPercent * 0.22).toFixed(1)}%</td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">—</td>
                    <td className="py-2.5 px-3 text-xs text-text-secondary">
                      {activePl.toLocaleString()} active · {offlinePl.toLocaleString()} offline
                    </td>
                    <td className="py-2.5 px-3 text-right text-text-secondary">{c.alarms}</td>
                  </tr>
                );
              })}

              {/* Fallback: non-PGVCL DISCOM drill-down */}
              {level === 'discom' && selectedDISCOM !== 'PGVCL' && (
                <tr>
                  <td colSpan={12} className="py-8 text-center text-xs text-text-tertiary">
                    Circle-level data not available for {selectedDISCOM} in this demo.
                  </td>
                </tr>
              )}

              {level === 'national' && visibleDISCOMs.length === 0 && (
                <tr>
                  <td colSpan={12} className="py-10 text-center text-xs text-text-tertiary">
                    No DISCOMs match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
